import { getCachedUserDetails } from './userCache'

// Transform API response to match UI structure
export const transformApiData = (apiData) => {
  if (!apiData) return null

  // Sort test cases by orderIndex
  const sortedTestCases = [...apiData.testCases].sort((a, b) => a.orderIndex - b.orderIndex)

  // Transform test cases to match UI format
  const transformedTestCases = sortedTestCases.map(testCase => ({
    id: testCase.id,
    type: 'test',
    number: testCase.orderIndex,
    orderIndex: testCase.orderIndex, // Preserve orderIndex for reordering
    testCaseKey: testCase.testCaseKey || '', // Jira-style key (e.g., "POJ-TC-1")
    title: testCase.testCaseName,
    description: testCase.description || '',
    priority: testCase.priority,
    assignee: testCase.assignee?.accountId || 'Unassigned',
    tags: testCase.labels?.map(label => label.name) || [],
    steps: testCase.testSteps?.length || 0,
    attachmentCount: testCase.attachmentCount || 0,
    commentCount: testCase.commentCount || 0,
    expanded: false, // Will be managed by Redux state
    testSteps: testCase.testSteps ? transformTestSteps(testCase.testSteps) : null,
    executions: testCase.executions || {}
  }))

  // Transform assignee data - normalize to a consistent format
  // API may return: { accountId, displayName, avatarUrl } or just accountId string
  let transformedAssignee = null
  if (apiData.assignee) {
    if (typeof apiData.assignee === 'object') {
      // Full assignee object from API
      transformedAssignee = {
        accountId: apiData.assignee.accountId,
        displayName: apiData.assignee.displayName || 'Unknown User',
        avatarUrl: apiData.assignee.avatarUrl || apiData.assignee.avatarUrls?.['24x24'] || null
      }
    } else if (typeof apiData.assignee === 'string') {
      // Just accountId string - check cache first for immediate display,
      // otherwise enrichment effect in App.jsx will fetch from Jira later
      const cached = getCachedUserDetails(apiData.assignee)
      transformedAssignee = {
        accountId: apiData.assignee,
        displayName: cached?.displayName || null,
        avatarUrl: cached?.avatarUrl || null
      }
    }
  }

  return {
    testScenarioId: apiData.testScenarioId,
    scenarioKey: apiData.scenarioKey || '', // Jira issue key (e.g., "KAN-1")
    testScenarioName: apiData.testScenarioName,
    testScenarioDescription: apiData.description || '', // ADD: Extract description from API
    testScenarioPriority: apiData.priority || 'Medium', // ADD: Extract priority from API
    testScenarioAssignee: transformedAssignee, // ADD: Extract assignee from API
    testRuns: apiData.testRuns || [],
    testCases: transformedTestCases,
    jiraIssues: apiData.jiraIssues || []
  }
}

// Transform test steps
const transformTestSteps = (testSteps) => {
  if (!testSteps) return null

  // Sort by orderIndex
  const sortedSteps = [...testSteps].sort((a, b) => a.orderIndex - b.orderIndex)

  return sortedSteps.map(step => ({
    id: step.id,
    number: step.orderIndex,
    orderIndex: step.orderIndex, // Preserve orderIndex for reordering
    title: step.stepAction,
    testData: step.testData || '',
    expected: step.expectedResult,
    attachmentCount: step.attachmentCount || 0,
    commentCount: step.commentCount || 0,
    executions: step.executions || {}
  }))
}

// Get execution status for a specific run
export const getExecutionStatus = (executions, runId) => {
  if (!executions || !executions[runId]) {
    return null
  }
  
  return executions[runId]
}

// Format date for display
export const formatDate = (dateString) => {
  if (!dateString) return '-'

  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    return '-'
  }
}

/**
 * AGGREGATE SCENARIO STATUS COUNTS
 *
 * Aggregates execution status counts across ALL test runs.
 * Counts both test case and test step executions for every run.
 *
 * @param {Array} testCases - Array of test case objects with executions
 * @param {Array} testRuns - Array of test run objects
 * @returns {object} Map of status name to count (e.g., { 'Pass': 25, 'Fail': 10, 'Not Run': 15 })
 */
export const aggregateScenarioStatusCounts = (testCases, testRuns) => {
  const counts = {}

  // Iterate through ALL test runs
  testRuns.forEach(run => {
    testCases.forEach(testCase => {
      // Count test case execution for this run
      const tcExec = testCase.executions?.[run.id] || testCase.executions?.[String(run.id)]
      const tcStatus = tcExec?.statusName || 'Not Run'
      counts[tcStatus] = (counts[tcStatus] || 0) + 1

      // Count all test step executions for this run
      testCase.testSteps?.forEach(step => {
        const stepExec = step.executions?.[run.id] || step.executions?.[String(run.id)]
        const stepStatus = stepExec?.statusName || 'Not Run'
        counts[stepStatus] = (counts[stepStatus] || 0) + 1
      })
    })
  })

  return counts
}

/**
 * AGGREGATE RUN STATUS COUNTS
 *
 * Aggregates execution status counts for a specific test run.
 * Counts both test case executions AND test step executions.
 *
 * @param {Array} testCases - Array of test case objects with executions
 * @param {string|number} runId - ID of the test run to aggregate
 * @returns {object} Map of status name to count (e.g., { 'Pass': 5, 'Fail': 2 })
 */
export const aggregateRunStatusCounts = (testCases, runId) => {
  const counts = {}

  testCases.forEach(testCase => {
    // Count test case - use status if exists, otherwise "Not Run"
    const tcExec = testCase.executions?.[runId] || testCase.executions?.[String(runId)]
    const tcStatus = tcExec?.statusName || 'Not Run'
    counts[tcStatus] = (counts[tcStatus] || 0) + 1

    // Count all test steps - use status if exists, otherwise "Not Run"
    testCase.testSteps?.forEach(step => {
      const stepExec = step.executions?.[runId] || step.executions?.[String(runId)]
      const stepStatus = stepExec?.statusName || 'Not Run'
      counts[stepStatus] = (counts[stepStatus] || 0) + 1
    })
  })

  return counts
}
